import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Avatar } from "../Auth/AvatarPicker";
import { api } from "../../lib/api";
import "./Profile.css";

// ─── Badge definitions (mirror of workers/src/data/badges.ts) ─────────────────

type BadgeCategory = "A" | "B" | "C";

interface BadgeDef {
  id: string;
  category: BadgeCategory;
  name: string;
  description: string;
  icon: string;
}

const BADGES: BadgeDef[] = [
  { id: "mahtit",         category: "A", name: "Máhttit",       description: "Lær 50 ord",                     icon: "📚" },
  { id: "diedalas",       category: "A", name: "Dieđalaš",      description: "Lær 200 ord",                    icon: "🎓" },
  { id: "mahttalas",      category: "A", name: "Máhttálaš",     description: "Lær 500 ord",                    icon: "⭐" },
  { id: "duojars",        category: "B", name: "Duojárs",       description: "Kombiner 3 ressurstyper",        icon: "🔨" },
  { id: "meaccar",        category: "B", name: "Meahccár",      description: "Kombiner skinn + horn + snøre",  icon: "🏹" },
  { id: "matkeolmmaj",    category: "C", name: "Mátkeolmmái",   description: "Fullfør 3 steder",               icon: "🗺️" },
  { id: "mailmmeolmmaj",  category: "C", name: "Máilmmeolmmái", description: "Fullfør alle steder",            icon: "🌍" },
];

const BADGE_CATEGORIES: { id: BadgeCategory; label: string }[] = [
  { id: "A", label: "Kunnskap"  },
  { id: "B", label: "Håndverk" },
  { id: "C", label: "Reise"    },
];

const RESOURCE_META: Record<string, { name: string; emoji: string }> = {
  muorji:     { name: "Multebær", emoji: "🫐" },
  muorra:     { name: "Ved",      emoji: "🪵" },
  gámasgárvi: { name: "Skinn",    emoji: "🦌" },
  náhppi:     { name: "Snøre",    emoji: "🧵" },
  silki:      { name: "Ullgarn",  emoji: "🧶" },
  sarvi:      { name: "Horn",     emoji: "🦴" },
};

const RESOURCE_IDS = Object.keys(RESOURCE_META);

// ─── Types ─────────────────────────────────────────────────────────────────────

interface ProfileData {
  user: { id: string; username: string; avatar_id: string; gold: number };
  badges: string[];
  inventory: { resource_id: string; quantity: number }[];
  wordCount: number;
  completedLevels: number;
}

// ─── Helper: highest badge per category ───────────────────────────────────────

function topBadgePerCategory(earnedIds: Set<string>): BadgeDef[] {
  return BADGE_CATEGORIES.flatMap(({ id: cat }) => {
    const catBadges = BADGES.filter((b) => b.category === cat && earnedIds.has(b.id));
    return catBadges.length > 0 ? [catBadges[catBadges.length - 1]] : [];
  });
}

// ─── ProfileScreen ─────────────────────────────────────────────────────────────

export function ProfileScreen() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    api.get<ProfileData>("/profile", token).then((res) => {
      if (cancelled) return;
      if (res.ok) setProfile(res.data);
      else setError(res.error ?? "Kunne ikke laste profil");
    });

    return () => { cancelled = true; };
  }, [token]);

  if (error) {
    return (
      <div className="profile-page">
        <div className="profile-loading">{error}</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="profile-page">
        <div className="profile-loading">Laster profil…</div>
      </div>
    );
  }

  const earnedSet = new Set(profile.badges);
  const topBadges = topBadgePerCategory(earnedSet);

  const invMap = Object.fromEntries(
    profile.inventory.map((e) => [e.resource_id, e.quantity])
  );

  return (
    <div className="profile-page">
      {/* Header */}
      <div className="profile-page__header">
        <button className="profile-page__back" onClick={() => navigate("/map")}>
          ←
        </button>
        <span className="profile-page__title">Profil</span>
      </div>

      <div className="profile-body">
        {/* Hero */}
        <div className="profile-hero">
          <div className="profile-hero__avatar">
            <Avatar avatarId={profile.user.avatar_id} size={96} />
          </div>
          <div className="profile-hero__name-row">
            <span className="profile-hero__username">{profile.user.username}</span>
            {topBadges.length > 0 && (
              <span className="profile-hero__badge-icons" aria-label="Høyeste badges">
                {topBadges.map((b) => (
                  <span key={b.id} title={b.name}>{b.icon}</span>
                ))}
              </span>
            )}
          </div>
          <div className="profile-hero__gold">
            <span>🪙</span>
            <span>{profile.user.gold}</span>
          </div>
        </div>

        {/* Stats */}
        <div className="profile-stats">
          <div className="stat-card">
            <span className="stat-card__value">{profile.wordCount}</span>
            <span className="stat-card__label">Ord lært</span>
          </div>
          <div className="stat-card">
            <span className="stat-card__value">{profile.completedLevels}</span>
            <span className="stat-card__label">Brett fullført</span>
          </div>
        </div>

        {/* Resources */}
        <p className="section-label">Ressurser</p>
        <div className="resources-grid">
          {RESOURCE_IDS.map((id) => {
            const qty = invMap[id] ?? 0;
            const meta = RESOURCE_META[id];
            return (
              <div
                key={id}
                className={`resource-card${qty === 0 ? " resource-card--empty" : ""}`}
              >
                <span className="resource-card__icon">{meta.emoji}</span>
                <span className="resource-card__name">{meta.name}</span>
                <span className="resource-card__qty">×{qty}</span>
              </div>
            );
          })}
        </div>

        {/* Badges */}
        <p className="section-label">Badges</p>
        <div className="badge-categories">
          {BADGE_CATEGORIES.map(({ id: cat, label }) => {
            const catBadges = BADGES.filter((b) => b.category === cat);
            return (
              <div key={cat}>
                <p className={`badge-col__label badge-col__label--${cat}`}>{label}</p>
                <div className="badge-col__items">
                  {catBadges.map((badge) => {
                    const isEarned = earnedSet.has(badge.id);
                    return (
                      <div
                        key={badge.id}
                        className={`badge-card ${
                          isEarned ? `badge-card--earned-${cat}` : "badge-card--locked"
                        }`}
                      >
                        <span className="badge-card__icon">{badge.icon}</span>
                        <span className="badge-card__name">{badge.name}</span>
                        <span className="badge-card__desc">{badge.description}</span>
                        {!isEarned && <span className="badge-card__lock">🔒</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
